import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Sparkle, StickerNote, FanDash } from "../components/Decor2";
import { WavyBreak, FramedImage, RotatingLogo, WavyCard } from "../components/Wavy";
import { useSection } from "../context/SiteContext";

export const Pill = ({ to, href, children, dark = true, testid }) => {
  const cls = `group inline-flex items-center gap-3 rounded-full pl-7 pr-3 py-4 font-serif-display uppercase text-[13px] md:text-sm tracking-[0.18em] font-semibold transition-transform hover:-translate-y-0.5
    ${dark ? "bg-plum text-cream" : "bg-cream text-plum"}
    ring-2 ring-offset-4 ring-offset-cream
    ${dark ? "ring-plum-deep/30" : "ring-magenta/40"}`;
  const inner = (
    <>
      {children}
      <span className={`rounded-full w-9 h-9 flex items-center justify-center ${dark ? "bg-cream/15 group-hover:bg-cream/25" : "bg-magenta/10 group-hover:bg-magenta/20"}`}>
        <ArrowRight className="w-4 h-4" />
      </span>
    </>
  );
  if (href) return <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className={cls}>{inner}</a>;
  return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
};

/* 1 — MAIN BANNER (admin editable) */
const MainBanner = () => {
  const { data: hero } = useSection("home_hero");
  return (
    <section className="relative bg-cream pt-10 md:pt-16 pb-16 md:pb-20 px-6 md:px-10 overflow-hidden" data-testid="home-main-banner">
      <Sparkle className="absolute top-20 left-12 w-7 h-7" color="#EC2675" />
      <Sparkle className="absolute top-36 right-24 w-5 h-5" color="#4ECDE9" />
      <Sparkle className="absolute bottom-20 right-12 w-6 h-6" color="#FFD23F" />

      <div className="max-w-[1400px] mx-auto text-center">
        <div className="flex items-center justify-center gap-3 mb-6">
          <FanDash color="#EC2675" />
          <span className="font-serif-display uppercase tracking-[0.3em] text-magenta text-[11px] md:text-sm font-semibold">
            {hero.kicker || "HERITAGE DAHLIAS NZ × THE ETCHERY"}
          </span>
          <FanDash color="#EC2675" flip />
        </div>

        <h1 className="font-serif-display uppercase text-plum-deep leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl xl:text-[5.5rem]" data-testid="home-hero-title">
          {hero.title || "OUR CUSTOM GARDEN CREATIONS"}
        </h1>

        <div className="mt-5 flex items-center justify-center gap-6 flex-wrap">
          {hero.badge1 && <StickerNote rotate={-7} color="#EC2675" size="text-2xl md:text-4xl">{hero.badge1}</StickerNote>}
          {hero.badge2 && <StickerNote rotate={5} color="#4ECDE9" size="text-xl md:text-3xl">{hero.badge2}</StickerNote>}
        </div>

        <div className="mt-10 md:mt-14 mx-auto w-full max-w-[380px] md:max-w-[560px]">
          <RotatingLogo src={hero.image} alt="The Bloom Foundry" testid="home-hero-logo" />
        </div>

        <p className="mt-10 text-plum-deep/80 text-base md:text-xl max-w-2xl mx-auto">
          {hero.subtitle}
        </p>

        <div className="mt-9 flex flex-wrap gap-4 justify-center">
          <Pill to={hero.cta_link || "/workshop"} testid="hero-cta">{hero.cta_label || "VISIT THE WORKSHOP"}</Pill>
        </div>
      </div>
    </section>
  );
};

/* 2 — FOUNDRY WORKSHOP tile */
const WorkshopTile = () => {
  const { data: ws } = useSection("home_workshop");
  return (
    <section className="relative bg-magenta py-16 md:py-24 px-6 md:px-10 overflow-hidden text-cream" data-testid="home-workshop-tile">
      <Sparkle className="absolute top-10 right-1/4 w-6 h-6" color="#FFD23F" />
      <Sparkle className="absolute bottom-12 left-14 w-5 h-5" color="#FDFBF7" />
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 text-center lg:text-left">
          <span className="font-serif-display uppercase tracking-[0.3em] text-cream/85 text-[11px] md:text-sm">BUILD YOUR OWN</span>
          <h2 className="font-serif-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-4" data-testid="home-workshop-title">
            {ws.title || "FOUNDRY WORKSHOP"}
          </h2>
          <p className="mt-6 text-cream/95 text-base md:text-lg max-w-lg mx-auto lg:mx-0">{ws.subtitle}</p>
          <div className="mt-9 flex flex-wrap gap-4 justify-center lg:justify-start items-center">
            <Pill to="/workshop" dark={false} testid="home-workshop-cta">{ws.cta_label || "BUILD YOUR TAGS"}</Pill>
            <StickerNote rotate={-6} color="#FDFBF7" size="text-2xl md:text-3xl">your words, your tag</StickerNote>
          </div>
        </div>
        <div className="lg:col-span-5 max-w-[420px] mx-auto w-full">
          <WavyCard src={ws.image} alt="Custom plant tags" bg="#6E2470" testid="home-workshop-image" />
        </div>
      </div>
    </section>
  );
};

/* 3 — ABOUT US small banner */
const AboutTile = () => {
  const { data: about } = useSection("home_about");
  return (
    <section className="relative bg-cream py-14 md:py-20 px-6 md:px-10" data-testid="home-about-tile">
      <div className="max-w-[1100px] mx-auto grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-4 max-w-[280px] mx-auto w-full">
          <FramedImage src={about.image} alt="The Etchery & Co" bg="#4ECDE9" contain testid="home-about-image" />
        </div>
        <div className="md:col-span-8 text-center md:text-left">
          <span className="font-serif-display uppercase tracking-[0.3em] text-sky-deep text-[11px] md:text-sm font-semibold">WHO WE ARE</span>
          <h2 className="font-serif-display uppercase text-plum-deep text-3xl md:text-5xl mt-3 leading-tight" data-testid="home-about-title">
            {about.title || "ABOUT US"}
          </h2>
          <p className="mt-5 text-plum-deep/80 text-base md:text-lg max-w-xl mx-auto md:mx-0">{about.body}</p>
          <div className="mt-8 flex justify-center md:justify-start">
            <Pill to="/contact" testid="home-about-cta">{about.cta_label || "MEET THE MAKERS"}</Pill>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => (
  <div className="bg-cream">
    <MainBanner />
    <WavyBreak color="#EC2675" />
    <WorkshopTile />
    <WavyBreak color="#4ECDE9" />
    <AboutTile />
  </div>
);

export default Home;
